import { Space, Tag, Select, Typography, Tooltip } from 'antd';
import type { SelectProps } from 'antd';
import type { CSSProperties, FC, ReactNode } from 'react';

import type {
  PanelTemplateProps,
  TemplateFilterChip,
  TemplateSortOption,
} from '../system/templateTypes';
import type { Item } from '@eshop/types/catalogue.types.ts';
import { getImageUrl, IMG_NOT_FOUND } from '@tools/utils/imageUtils.tsx';
import {
  getCatalogueOfferPresentation,
  renderCatalogueOfferTooltip,
} from './offerPresentation';

const { Text } = Typography;

export type CatalogueImageVariant = 'card' | 'banner' | 'hero';

const DEFAULT_IMAGE_SIZES: Record<CatalogueImageVariant, string> = {
  card:
    '(min-width: 1536px) calc((100vw - 5 * 24px) / 6), ' +
    '(min-width: 1280px) calc((100vw - 4 * 24px) / 5), ' +
    '(min-width: 1024px) calc((100vw - 3 * 24px) / 4), ' +
    '(min-width: 768px) calc((100vw - 2 * 16px) / 3), ' +
    'calc(50vw - 12px)',
  banner:
    '(min-width: 1536px) 1280px, (min-width: 1280px) 1120px, (min-width: 768px) 720px, 100vw',
  hero: '(min-width: 1536px) 1440px, (min-width: 1280px) 1240px, 100vw',
};

export interface BuildCatalogueSizesOptions {
  variant?: CatalogueImageVariant;
  fallback?: string;
}

export const buildCatalogueSizes = (
  options: BuildCatalogueSizesOptions = {},
): string => {
  const { variant = 'card', fallback } = options;
  if (fallback && fallback.trim()) {
    return fallback.trim();
  }
  return DEFAULT_IMAGE_SIZES[variant];
};

export interface BuildCatalogueSrcSetOptions {
  provided?: string;
  derived?: string;
}

export const buildCatalogueSrcSet = ({
  provided,
  derived,
}: BuildCatalogueSrcSetOptions): string | undefined => {
  const candidates = [provided, derived]
    .filter((candidate): candidate is string => Boolean(candidate && candidate.trim()))
    .flatMap((candidate) => candidate.split(',').map((entry) => entry.trim()))
    .filter(Boolean);

  if (!candidates.length) {
    return undefined;
  }

  const unique = Array.from(new Set(candidates));
  return unique.join(', ');
};

export const toGutterPair = (value?: PanelTemplateProps['layout']['gutter']): [number, number] => {
  if (Array.isArray(value)) {
    return [value[0] ?? 24, value[1] ?? value[0] ?? 32];
  }
  if (typeof value === 'number') {
    return [value, value];
  }
  return [24, 32];
};

export interface FilterChipRenderOptions {
  containerClassName?: string;
  chipClassName?: string;
}

export const renderFilterChips = (
  chips: TemplateFilterChip[] | undefined,
  { containerClassName = 'catalogue-toolbar__chips', chipClassName = 'catalogue-chip' }: FilterChipRenderOptions = {},
): ReactNode => {
  if (!chips?.length) {
    return null;
  }

  return (
    <Space wrap size={[8, 8]} className={containerClassName}>
      {chips.map((chip) => (
        <Tag
          key={chip.key}
          className={chipClassName}
          closable={Boolean(chip.onRemove)}
          onClose={(event) => {
            event.preventDefault();
            chip.onRemove?.();
          }}
        >
          {chip.icon}
          <span>{chip.label}</span>
        </Tag>
      ))}
    </Space>
  );
};

export const renderSortSelect = (
  sort: PanelTemplateProps['sort'] | undefined,
  className?: string,
  placeholder = 'Ordenar',
): ReactNode => {
  if (!sort?.options?.length) {
    return null;
  }

  const options: SelectProps['options'] = sort.options.map((option: TemplateSortOption) => ({
    value: option.key,
    label: option.label,
  }));

  return (
    <Select
      className={className}
      options={options}
      value={sort.activeKey}
      onChange={sort.onChange}
      size="middle"
      placeholder={placeholder}
    />
  );
};

export interface MediaImageProps {
  item?: Item;
  aspect?: string;
  className?: string;
  imageClassName?: string;
  src?: string;
  srcSet?: string;
  sizes?: string;
  alt?: string;
  loading?: 'lazy' | 'eager';
  decoding?: 'auto' | 'async' | 'sync';
  fetchPriority?: 'high' | 'low' | 'auto';
  objectFit?: CSSProperties['objectFit'];
  variant?: CatalogueImageVariant;
}

export const parseAspectValue = (value?: string | null): CSSProperties['aspectRatio'] | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const slashMatch = trimmed.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (slashMatch) {
    const width = Number(slashMatch[1]);
    const height = Number(slashMatch[2]);
    if (Number.isFinite(width) && Number.isFinite(height) && height > 0) {
      return `${width} / ${height}`;
    }
  }

  const colonMatch = trimmed.match(/^(\d+)\s*:\s*(\d+)$/);
  if (colonMatch) {
    const width = Number(colonMatch[1]);
    const height = Number(colonMatch[2]);
    if (Number.isFinite(width) && Number.isFinite(height) && height > 0) {
      return `${width} / ${height}`;
    }
  }

  const legacyMatch = trimmed.match(/aspect_(\d+)-(\d+)/i);
  if (legacyMatch) {
    const width = Number(legacyMatch[1]);
    const height = Number(legacyMatch[2]);
    if (Number.isFinite(width) && Number.isFinite(height) && height > 0) {
      return `${width} / ${height}`;
    }
  }

  return undefined;
};

export const MediaImage: FC<MediaImageProps> = ({
  item,
  aspect,
  className,
  imageClassName,
  src,
  srcSet,
  sizes,
  alt,
  loading = 'lazy',
  decoding,
  fetchPriority,
  objectFit = 'cover',
  variant = 'card',
}) => {
  const primaryImage = item?.images?.[0];
  const derived = item ? getImageUrl(primaryImage) : { srcDefault: src, srcSet };
  const resolvedSrc = src ?? derived.srcDefault ?? IMG_NOT_FOUND;
  const resolvedSrcSet = buildCatalogueSrcSet({ provided: srcSet, derived: derived.srcSet });
  const resolvedAlt = alt ?? primaryImage?.caption ?? item?.name ?? '';
  // const aspectRatio = parseAspectValue(aspect);
  const computedSizes = buildCatalogueSizes({ variant, fallback: sizes });
  const resolvedSizes = resolvedSrcSet ? computedSizes : sizes?.trim();
  const resolvedFetchPriority = fetchPriority ?? (loading === 'eager' ? 'high' : undefined);
  const resolvedDecoding = decoding ?? (loading === 'eager' ? 'sync' : 'async');

  return (
    <div
      className={`${ className ?? 'catalogue-card__media'} contenedor ${aspect}`}
      // style={aspectRatio ? ({ aspectRatio } as CSSProperties) : undefined}
    >
      <img
        className={`${imageClassName ?? 'catalogue-card__media-image'} contenido`}
        src={resolvedSrc}
        srcSet={resolvedSrcSet}
        sizes={resolvedSizes}
        alt={resolvedAlt}
        loading={loading}
        decoding={resolvedDecoding}
        fetchPriority={resolvedFetchPriority}
        style={{ width: '100%', height: '100%', objectFit }}
      />
    </div>
  );
};

export interface PriceRenderOptions {
  containerClassName?: string;
  priceClassName?: string;
  currentClassName?: string;
  originalClassName?: string;
}

export const renderPrice = (
  item: Item,
  {
    containerClassName = 'catalogue-card__pricing',
    priceClassName = 'catalogue-card__price',
    currentClassName = 'catalogue-card__price--current',
    originalClassName = 'catalogue-card__price--original',
  }: PriceRenderOptions = {},
): ReactNode => {
  const offer = getCatalogueOfferPresentation(item);
  const formatted = offer.formattedPrice;
  if (!formatted) {
    return <Text className={`${priceClassName} ${priceClassName}--empty`}>—</Text>;
  }

  const hasDiscount = offer.hasDiscountedPrice && Boolean(offer.formattedDiscountedPrice);
  const content = (
    <Space
      orientation="vertical"
      size={0}
      className={`${containerClassName}${hasDiscount ? ' catalogue-card__pricing--with-offer' : ''}`}
    >
      {hasDiscount && (
        <Text className={`${priceClassName} ${originalClassName}`}>{formatted.formattedPrice}</Text>
      )}
      {hasDiscount && offer.badgeText && (
        <Text className="catalogue-card__price--condition">{offer.badgeText}</Text>
      )}
      <Text className={`${priceClassName} ${hasDiscount ? currentClassName : ''}`}>
        {hasDiscount ? offer.formattedDiscountedPrice : formatted.formattedPrice}
      </Text>
    </Space>
  );

  if (!hasDiscount) {
    return content;
  }

  return (
    <Tooltip title={renderCatalogueOfferTooltip(offer)} color="magenta" placement="top">
      {content}
    </Tooltip>
  );
};
